import BaseFormatter from './base'
import { castStrengthToVerdict, has } from '../../helpers'
import {
  Signal,
  SignalGroup,
  SignalGroups,
  SignalGroupMitreData,
  SignalGroupMitreTechnique,
  TechniquesGroupsMap,
  TagType,
  MitreTactic,
  SignalVerdict,
  SignalGroupMbcData,
  SignalGroupMbcMethod,
  SignalGroupMbcMethodData,
} from '../../types'
import { AssetsType, MbcBehavior, MbcMethod, MbcObjective, MitreTechnique } from 'app/modules/shared/types'
import { inc } from 'app/modules/shared/helpers'
import { ReportOverviewFilterStat } from '../../features/overview-report/types'
import { ReportFilterSection, ReportFilterSectionType } from '../../components/filter/types'
import { detectThreatIndicatorSection } from '../../features/overview-report/helpers'
import { combineSignalGroups } from './helper'

/**
 * Format report response data for Threat Indicators page
 */
export default class ThreatIndicatorsFormatter extends BaseFormatter {
  static assetsData: AssetsType = { mitreAttck: {}, mitreAtlas: {}, mbc: {} } as AssetsType
  underscoreRegexp = /_/g
  sectionsCache: { [key: string]: boolean } = {}

  /**
   * Format original report data
   *
   * @param data
   * @param report
   */
  format(data: { [key: string]: any }, report: { [key: string]: any }): void {
    this.sectionsCache = {}
    const result: { [key: string]: any } = {}

    const combinedSignalGroups = combineSignalGroups(data.allSignalGroups, data.resources)

    if (combinedSignalGroups.length > 0) {
      this.formatAllSignalGroups(combinedSignalGroups, result)
    }

    report.threatIndicators = result
  }

  /**
   * Apply format to a signal groups field.
   *
   * Performs the following:
   * - splits signal groups into columns by signal strength
   * - maps MITRE techniques to signal groups
   * - formats and simplifies data
   *
   * @param {object} input     Data obtained from given field in report response
   * @param {object} output    Container to put formatted result in
   */
  formatAllSignalGroups(input: { [key: string]: any }[], output: { [key: string]: any }): void {
    const groups: SignalGroups = {}
    const techniquesGroupsMap: TechniquesGroupsMap = {}
    const stat: ReportOverviewFilterStat = {
      types: {
        verdict: {},
        origin: {},
        mitreAttck: {},
        mitreAtlas: {},
        mbc: {},
        tags: {},
      },
    }
    const filterSections: { [key: string]: ReportFilterSection } = {
      verdict: { type: ReportFilterSectionType.Verdict, name: 'verdict', items: [] },
      tags: { type: ReportFilterSectionType.List, name: 'tags', items: [] },
      mitreAttck: {
        type: ReportFilterSectionType.Flags,
        name: 'mitreAttck',
        title: 'mitre-attck-techniques',
        items: [],
      },
      mitreAtlas: {
        type: ReportFilterSectionType.Flags,
        name: 'mitreAtlas',
        title: 'mitre-atlas-techniques',
        items: [],
      },
      mbc: { type: ReportFilterSectionType.Flags, name: 'mbc', title: 'mbc-behaviors', items: [] },
      origin: { type: ReportFilterSectionType.Flags, name: 'origin', items: [] },
    }

    input
      .sort((a: any, b: any) => b.verdict.threatLevel - a.verdict.threatLevel)
      .forEach((groupData: { [key: string]: any }) => {
        const group = this.buildGroup(groupData)
        const verdict: SignalVerdict = group.verdict
        const typedGroups = groups[verdict] || []
        const existing = typedGroups.find((grp) => grp.id === group.id)

        if (existing) {
          this.mergeGroup(existing, group)
        } else {
          typedGroups.push(group)
          this.updateTechniquesGroupsMap(techniquesGroupsMap, group, verdict)
        }

        groups[verdict] = typedGroups
      })

    for (const verdict in groups) {
      const verdictGroups = groups[verdict as keyof typeof groups]
      for (const group of verdictGroups || []) {
        this.gatherFilterStat(stat, group)
        this.gatherFilterSections(filterSections, group)
      }
    }

    output.groups = groups
    output.techniques_groups_map = techniquesGroupsMap
    output.filter = {
      stat,
      sections: Object.values(filterSections),
    }
  }

  /**
   * Build data for signal group
   *
   * @param {object} data
   */
  buildGroup(data: { [key: string]: any }): SignalGroup {
    const verdict = castStrengthToVerdict(data.verdict.threatLevel)
    const [signals, origins] = Array.isArray(data.signals) ? this.buildSignals(data.signals) : [{}, {}]

    const group: SignalGroup = {
      id: data.identifier,
      uniqueId: data.identifier + '-' + verdict,
      type: detectThreatIndicatorSection(data.identifier),
      description: data.description,
      verdict: verdict,
      signals: signals,
      origins: Object.keys(origins),
      strength: {
        average: data.verdict.threatLevel,
        peak: data.peakSignalStrength,
      },
    }

    if (typeof data.allMitreTechniques !== 'undefined') {
      group.mitreAttck = this.buildMitreAttckTechniques(data.allMitreTechniques)
    }
    if (typeof data.allAtlasTechniques !== 'undefined') {
      group.mitreAtlas = this.buildMitreAtlasTechniques(data.allAtlasTechniques)
    }
    if (typeof data.allMbcBehaviors !== 'undefined') {
      group.mbcBehaviors = this.buildMbcData(data.allMbcBehaviors)
    }
    if (Array.isArray(data.allMbcMethods) && data.allMbcMethods.length) {
      group.mbcMethods = this.buildMbcMethods(data.allMbcMethods)
    }
    if (Array.isArray(data.allTags) && data.allTags.length) {
      group.tags = data.allTags
    }

    return group
  }

  /**
   * Merge signals from other signal group into the current signal group
   */
  mergeSignals(group: SignalGroup, other: SignalGroup): void {
    const existingSignals = group.signals
    const otherSignals = other.signals
    for (const origin in otherSignals) {
      const signals = otherSignals[origin]
      const existings = existingSignals[origin] ?? []
      const existingObj: { [key: string]: Signal } = {}
      for (const sig of existings) {
        existingObj[sig.originId] = sig
      }

      for (const signal of signals) {
        if (existingObj[signal.originId]?.description !== signal.description) {
          existings.push(signal)
          existingObj[signal.originId] = signal
        }
      }

      if (existings.length) {
        existingSignals[origin] = existings
      }
    }
  }

  /**
   * Merge tags from other signal group into the current signal group
   */
  mergeTags(group: SignalGroup, other: SignalGroup): void {
    const existingTags = group.tags ?? []
    const existingTagObj: { [key: string]: TagType } = {}
    for (const tag of existingTags) {
      if (tag.tag?.name) {
        existingTagObj[tag.tag?.name] = tag
      }
    }
    const otherTags = other.tags ?? []
    for (const tag of otherTags) {
      if (!tag.tag?.name || existingTagObj[tag.tag.name]?.tag?.name !== tag.tag.name) {
        existingTags.push(tag)
      }
    }

    if (existingTags.length) {
      group.tags = existingTags
    }
  }

  /**
   * Merge MITRE or MBC data from other signal group into the current signal group
   */
  mergeMitreOrMbcData(
    group: SignalGroup,
    other: SignalGroup,
    fieldName: 'mitreAttck' | 'mitreAtlas' | 'mbcMethods' | 'mbcBehaviors'
  ): void {
    const existingData = group[fieldName] ?? {}
    const otherData = other[fieldName] ?? {}
    for (const id in otherData) {
      if (id in existingData) continue
      existingData[id] = otherData[id]
    }

    if (Object.keys(existingData).length) {
      // Use this weird syntax to avoid TypeScript warning for dynamic field assignment
      Object.assign(group, { [fieldName]: existingData })
    }
  }

  /**
   * Merge all data from other signal group into the current signal group
   */
  mergeGroup(group: SignalGroup, other: SignalGroup): void {
    this.mergeSignals(group, other)
    this.mergeTags(group, other)
    this.mergeMitreOrMbcData(group, other, 'mitreAtlas')
    this.mergeMitreOrMbcData(group, other, 'mitreAttck')
    this.mergeMitreOrMbcData(group, other, 'mbcMethods')
    this.mergeMitreOrMbcData(group, other, 'mbcBehaviors')
  }

  /**
   * Add signal group to map of MITRE techniques IDs -> signal groups
   *
   * @param {object} mapping Map of MITRE techniques IDs to signal groups
   * @param {object} group Signal group
   * @param {string} type Type of signal group by strength
   */
  updateTechniquesGroupsMap(mapping: TechniquesGroupsMap, group: SignalGroup, verdict: SignalVerdict): void {
    if (typeof group.mitreAttck === 'undefined') {
      return
    }

    const techniquesIds = Object.keys(group.mitreAttck)
    techniquesIds.forEach((id: string) => {
      if (typeof mapping[id] === 'undefined') {
        mapping[id] = { count: {}, groups: [] }
      }

      let count = mapping[id]?.count[verdict] || 0
      count++

      mapping[id].count[verdict] = count
      mapping[id].groups.push(group.id)
    })
  }

  /**
   * Format and simplify MITRE techniques
   *
   * @param {array} data  MITRE techniques for single signal group
   */
  buildMitreAtlasTechniques(data: { [key: string]: any }): { [key: string]: any } {
    const result: SignalGroupMitreData = {}
    const techniquesDesc = this.getAllMitreTechniques('mitreAtlas')

    data.forEach((item: any) => {
      const technique: SignalGroupMitreTechnique = { name: item.technique_name }

      if (typeof item.relatedTactics !== 'undefined') {
        technique.tactic = item.relatedTactics?.[0]?.tactic_name
      }
      if (typeof techniquesDesc[item.technique_id] !== 'undefined') {
        technique.description = techniquesDesc[item.technique_id].description
      }

      result[item.technique_id] = technique
    })

    return result
  }

  /**
   * Format and simplify MITRE techniques
   *
   * @param {array} data  MITRE techniques for single signal group
   */
  buildMitreAttckTechniques(data: { [key: string]: any }): { [key: string]: any } {
    const result: SignalGroupMitreData = {}
    const techniquesDesc = this.getAllMitreTechniques('mitreAttck')

    data.forEach((item: any) => {
      const technique: SignalGroupMitreTechnique = { name: item.name }

      if (typeof item.relatedTactic !== 'undefined') {
        technique.tactic = item.relatedTactic.name
      }
      if (typeof techniquesDesc[item.ID] !== 'undefined') {
        technique.description = techniquesDesc[item.ID].description
      }

      result[item.ID] = technique
    })

    return result
  }

  /**
   * Return MITRE techniques as plain object {techniquesId: data}
   */
  getAllMitreTechniques(mitreTypeKey: 'mitreAttck' | 'mitreAtlas') {
    const mitreData = ThreatIndicatorsFormatter.assetsData[mitreTypeKey]
    const techniques: { [techniqueId: string]: MitreTechnique } = Object.values(mitreData || {}).reduce(
      (result: { [key: string]: any }, item: MitreTactic) => {
        return { ...result, ...item.techniques }
      },
      {}
    )

    return techniques
  }

  /**
   * Format and simplify MBC behaviors
   *
   * @param {array} data  MBC behaviors for single signal group
   */
  buildMbcData(data: { [key: string]: any }): { [key: string]: any } {
    const result: SignalGroupMbcData = {}
    const behaviorsDesc = this.getAllMbcBehaviors()

    data.forEach((item: any) => {
      const behaviorId: string = item.behavior_id
      const behavior: MbcBehavior = { id: behaviorId, name: item.behavior_name }
      const objective = item.relatedObjective || item.relatedObjectives?.[0]

      if (typeof objective !== 'undefined') {
        behavior.objective = objective.objective_name
      }
      if (typeof behaviorsDesc[behaviorId] !== 'undefined') {
        behavior.description = behaviorsDesc[behaviorId].description
      }

      result[behaviorId] = behavior
    })

    return result
  }

  /**
   * Return MBC behaviors as plain object {behaviorId: data}
   */
  getAllMbcBehaviors() {
    const mbcData = ThreatIndicatorsFormatter.assetsData.mbc
    const behaviors: { [behaviorId: string]: MbcBehavior } = Object.values(mbcData || {}).reduce(
      (result: { [key: string]: any }, item: MbcObjective) => {
        return { ...result, ...item.behaviors }
      },
      {}
    )

    return behaviors
  }

  /**
   * Return MBC methods as plain object {methodId: data} and behavior-to-objective map in a single pass
   */
  getAllMbcMethods(): [{ [methodId: string]: MbcMethod }, { [behaviorId: string]: string }] {
    const mbcData = ThreatIndicatorsFormatter.assetsData.mbc
    const methods: { [methodId: string]: MbcMethod } = {}
    const objectiveMap: { [behaviorId: string]: string } = {}
    for (const item of Object.values(mbcData || {})) {
      Object.assign(methods, item.methods)
      for (const behaviorId in item.behaviors || {}) {
        objectiveMap[behaviorId] = item.name
      }
    }
    return [methods, objectiveMap]
  }

  /**
   * Format and simplify MBC methods
   *
   * @param {array} data  MBC methods for single signal group
   */
  buildMbcMethods(data: { [key: string]: any }[]): SignalGroupMbcMethodData {
    const result: SignalGroupMbcMethodData = {}
    const behaviorsDesc = this.getAllMbcBehaviors()
    const [methodsDesc, behaviorObjectiveMap] = this.getAllMbcMethods()

    data.forEach((item: any) => {
      const methodId: string = item.method_id
      const dotIndex = methodId.lastIndexOf('.')
      const parentBehaviorId = dotIndex > -1 ? methodId.substring(0, dotIndex) : undefined

      const method: SignalGroupMbcMethod = { id: methodId, name: item.method_name }

      if (parentBehaviorId) {
        method.parentBehaviorId = parentBehaviorId
        if (behaviorsDesc[parentBehaviorId]) {
          method.parentBehaviorName = behaviorsDesc[parentBehaviorId].name
        }
        if (behaviorObjectiveMap[parentBehaviorId]) {
          method.objective = behaviorObjectiveMap[parentBehaviorId]
        }
      }

      if (methodsDesc[methodId]?.description) {
        method.description = methodsDesc[methodId].description
      }

      result[methodId] = method
    })

    return result
  }

  /**
   * Simplify and format signals
   *
   * @param {array} data  Signals
   */
  buildSignals(data: { [key: string]: any }[]) {
    const signals: { [key: string]: any } = {}
    const origins: { [origin: string]: boolean } = {}

    data.forEach((item: any) => {
      const origin = has(item, 'originType')
        ? item.originType.replace(this.underscoreRegexp, ' ').toLowerCase()
        : 'other'

      const signal: Signal = {
        strength: item.strength,
        description: this.splitString(item.signalReadable),
        origin,
        originId: item.originIdentifier,
      }

      if (Array.isArray(item.tags) && item.tags.length) {
        signal.tags = item.tags
      }

      if (item.additionalMessage) {
        signal.additionalMessage = item.additionalMessage
      }

      if (item.emulationEventID) {
        signal.emulationEventID = item.emulationEventID
      }

      if (!signals[origin]) {
        signals[origin] = []
      }
      signals[origin].push(signal)
      origins[origin] = true
    })

    return [signals, origins]
  }

  gatherFilterStat(stat: ReportOverviewFilterStat, group: SignalGroup) {
    const statTypes = stat.types

    for (const origin of group.origins || []) {
      statTypes.origin[origin] = inc(statTypes.origin[origin])
    }
    for (const techniqueId in group.mitreAttck || {}) {
      statTypes.mitreAttck[techniqueId] = inc(statTypes.mitreAttck[techniqueId])
    }
    for (const atlasId in group.mitreAtlas || {}) {
      statTypes.mitreAtlas[atlasId] = inc(statTypes.mitreAtlas[atlasId])
    }
    for (const behaviorId in group.mbcBehaviors || {}) {
      statTypes.mbc[behaviorId] = inc(statTypes.mbc[behaviorId])
    }
    for (const methodId in group.mbcMethods || {}) {
      statTypes.mbc[methodId] = inc(statTypes.mbc[methodId])
    }
    for (const tag of group.tags || []) {
      statTypes.tags[tag.tag.name] = inc(statTypes.tags[tag.tag.name])
    }

    statTypes.verdict[group.verdict] = inc(statTypes.verdict[group.verdict])
    stat.total = inc(stat.total)
  }

  gatherFilterSections(sections: { [key: string]: ReportFilterSection }, group: SignalGroup) {
    for (const techniqueId in group.mitreAttck || {}) {
      const technique = group.mitreAttck?.[techniqueId] as SignalGroupMitreTechnique

      if (typeof this.sectionsCache[techniqueId] === 'undefined') {
        sections.mitreAttck.items.push({ value: techniqueId, name: technique.name })
        this.sectionsCache[techniqueId] = true
      }
    }
    for (const techniqueId in group.mitreAtlas || {}) {
      const technique = group.mitreAtlas?.[techniqueId] as SignalGroupMitreTechnique

      if (typeof this.sectionsCache[techniqueId] === 'undefined') {
        sections.mitreAtlas.items.push({ value: techniqueId, name: technique.name })
        this.sectionsCache[techniqueId] = true
      }
    }
    for (const behaviorId in group.mbcBehaviors || {}) {
      const behavior = group.mbcBehaviors?.[behaviorId] as MbcBehavior

      if (typeof this.sectionsCache[behaviorId] === 'undefined') {
        sections.mbc.items.push({ value: behaviorId, name: behavior.name })
        this.sectionsCache[behaviorId] = true
      }
    }
    for (const methodId in group.mbcMethods || {}) {
      const method = group.mbcMethods?.[methodId] as SignalGroupMbcMethod

      if (typeof this.sectionsCache[methodId] === 'undefined') {
        sections.mbc.items.push({ value: methodId, name: method.name })
        this.sectionsCache[methodId] = true
      }
    }
    for (const origin of group.origins || []) {
      if (typeof this.sectionsCache[origin] === 'undefined') {
        sections.origin.items.push({ value: origin, name: origin })
        this.sectionsCache[origin] = true
      }
    }
    for (const tag of group.tags || []) {
      const tagName = tag.tag.name
      if (typeof this.sectionsCache[tagName] === 'undefined') {
        sections.tags.items.push({ value: tagName })
        this.sectionsCache[tagName] = true
      }
    }
  }

  splitString(text: string): Array<string> {
    if (!text) {
      return []
    }

    const len = text.length
    const arr = []
    let leveled = false
    let start = 0

    for (let i = 0; i < len; i++) {
      if (text[i] === '"') {
        if (leveled && i > 0 && text[i - 1] === '\\') {
          continue
        }
        arr.push(text.substring(start, i))
        start = i + 1
        leveled = !leveled
      }
    }

    if (len > start) {
      arr.push(text.substring(start, len))
    }

    return arr
  }
}
